import { TICKET_TYPE_LIST } from '../../utils/ticketType'
import { PRIORITY_META } from '../../utils/priorityMatrix'
import {
  NO_QUEUE,
  SLA_FILTER_OPTIONS,
  UNASSIGNED,
} from '../../utils/ticketTable'
import { employeeName } from '../../utils/employee'

/** Criterios que muestra el panel si no se indica otra cosa. */
const DEFAULT_FIELDS = [
  'queueId',
  'type',
  'priorityId',
  'responsibleId',
  'authorId',
  'sla',
  'createdFrom',
  'createdTo',
]

const personLabel = (person) => employeeName(person) || `#${person.value}`

/**
 * Criterios de búsqueda avanzados de tickets. Es presentacional: recibe los
 * valores, las opciones y notifica cada cambio.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.filters - Criterios actuales (ver `DEFAULT_TICKET_FILTERS`).
 * @param {(key: string, value: string) => void} props.onChange - Cambia un criterio.
 * @param {() => void} props.onClear - Limpia todos los criterios.
 * @param {{origins?: string[], responsibles?: Array<Object>, authors?: Array<Object>, statuses?: Array<Object>, queues?: Array<Object>}} props.options
 *   Opciones de los selectores (ver `useTicketFilters`).
 * @param {string[]} [props.fields] - Criterios a mostrar y en qué orden.
 * @returns {JSX.Element}
 */
const TicketTableFilters = ({
  filters,
  onChange,
  onClear,
  options = {},
  fields = DEFAULT_FIELDS,
}) => {
  const bind = (key) => ({
    value: filters[key],
    onChange: (e) => onChange(key, e.target.value),
  })

  /** Control de cada criterio, por clave de filtro. */
  const FIELD_CONTROLS = {
    queueId: () => (
      <select className="form-select form-select-sm" {...bind('queueId')}>
        <option value="">Todas</option>
        <option value={NO_QUEUE}>Sin cola</option>
        {(options.queues || []).map((q) => (
          <option key={q.id} value={q.id}>
            {q.name}
            {q.active ? '' : ' (inactiva)'}
          </option>
        ))}
      </select>
    ),
    statusId: () => (
      <select className="form-select form-select-sm" {...bind('statusId')}>
        <option value="">Todos</option>
        {(options.statuses || []).map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    ),
    origin: () => (
      <select className="form-select form-select-sm" {...bind('origin')}>
        <option value="">Todas</option>
        {(options.origins || []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    ),
    type: () => (
      <select className="form-select form-select-sm" {...bind('type')}>
        <option value="">Todos</option>
        {TICKET_TYPE_LIST.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
    ),
    priorityId: () => (
      <select className="form-select form-select-sm" {...bind('priorityId')}>
        <option value="">Todas</option>
        {Object.entries(PRIORITY_META).map(([id, meta]) => (
          <option key={id} value={id}>
            {meta.label}
          </option>
        ))}
      </select>
    ),
    responsibleId: () => (
      <select className="form-select form-select-sm" {...bind('responsibleId')}>
        <option value="">Todos</option>
        <option value={UNASSIGNED}>Sin asignar</option>
        {(options.responsibles || []).map((p) => (
          <option key={p.value} value={p.value}>
            {personLabel(p)}
          </option>
        ))}
      </select>
    ),
    authorId: () => (
      <select className="form-select form-select-sm" {...bind('authorId')}>
        <option value="">Todos</option>
        {(options.authors || []).map((p) => (
          <option key={p.value} value={p.value}>
            {personLabel(p)}
          </option>
        ))}
      </select>
    ),
    sla: () => (
      <select className="form-select form-select-sm" {...bind('sla')}>
        <option value="">Todos</option>
        {SLA_FILTER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ),
    createdFrom: () => (
      <input
        type="date"
        className="form-control form-control-sm"
        max={filters.createdTo || undefined}
        {...bind('createdFrom')}
      />
    ),
    createdTo: () => (
      <input
        type="date"
        className="form-control form-control-sm"
        min={filters.createdFrom || undefined}
        {...bind('createdTo')}
      />
    ),
  }

  const LABELS = {
    queueId: 'Cola',
    statusId: 'Estado',
    origin: 'Plataforma',
    type: 'Tipo',
    priorityId: 'Prioridad',
    responsibleId: 'Responsable',
    authorId: 'Autor',
    sla: 'SLA',
    createdFrom: 'Creado desde',
    createdTo: 'Creado hasta',
  }

  return (
    <div className="ticket-adv-filters">
      <div className="taf-grid">
        {fields.map((key) => (
          <div key={key}>
            <label className="taf-label">{LABELS[key]}</label>
            {FIELD_CONTROLS[key]()}
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-end mt-2">
        <button type="button" className="taf-clear" onClick={onClear}>
          <i className="fa-solid fa-filter-circle-xmark me-1"></i>Limpiar
          filtros
        </button>
      </div>

      <style>{`
        .taf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; }
        .taf-label { display: block; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: var(--gm-text-muted); margin-bottom: 3px; }
        .taf-clear { border: none; background: transparent; color: var(--gm-primary); font-size: 0.8rem; font-weight: 600; }
      `}</style>
    </div>
  )
}

export default TicketTableFilters
